import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Column, List, Icon } from '@pella/sash-framework';
import { getLocalStorage, setLocalStorage, removeLocalStorage } from '../../components/utilities/functions';
import { SEO, Image, CTA } from '../../components';
import { getShowroomByIP } from '../../actions/location-actions';
import { useLocation } from '../../context/LocationContext';
import { isValidArray } from '../../components/utilities/validations';
import SocialShare from '../../components/common/social-share';
import Tile from '../../templates/plp/results/tile';
import SDImageDetail from '../../components/common/structured-data/image-detail';
import { useRouteChange } from '../../hooks/use-route-change';

const ImageDetail = ({ data }) => {
	const { url, image, heading, description, filter_cta, ctas, detail_type, tiles } = data.contentstackImageDetail || {};
	const { setLocation } = useLocation();
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const [closeURL, setCloseUrl] = useState(null);
	const [position, setPostion] = useState({});
	const [prevURL, setPrevURL] = useState('');
	const [nextURL, setNextURL] = useState('');
	const [hover, setHover] = useState(null);

	useRouteChange({
		pageTitle: heading,
		pageType: '',
	});

	useEffect(() => {
		let imgData = getLocalStorage('imgDetailData');

		setCloseUrl(imgData?.currentUrl || data?.config?.return_url || '');

		if (imgData?.position) {
			setPostion({ ...imgData.position });
		}

		const slideData = imgData?.slideData;
		const pageIndex = isValidArray(slideData) ? slideData.indexOf(url) : -1;

		if (isValidArray(slideData) && pageIndex > 0) {
			setPrevURL(slideData[pageIndex - 1]);
		}

		if (isValidArray(slideData) && pageIndex > -1 && pageIndex < slideData.length - 1) {
			setNextURL(slideData[pageIndex + 1]);
		}

		// if landing on image-detail directly
		if (getLocalStorage('UserLocation') != null) {
			const userInfo = getLocalStorage('UserLocation');
			if (userInfo && locations.market === undefined) {
				setLocation(userInfo.postal_code);
			}
		} else {
			dispatch(getShowroomByIP());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	useEffect(() => {
		if (getLocalStorage('UserLocation') == null && locations?.showroom?.guest_location) {
			setLocation(locations.showroom.guest_location.postal_code);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [JSON.stringify(locations.showroom)]);

	function clearImgData() {
		const scrollPoint = {
			closeURL,
			position,
		};
		setLocalStorage('scrollPoint', scrollPoint);
		removeLocalStorage('imgDetailData');
	}

	const socialBlock = (
		<SocialShare
			url={url}
			media={image?.[0]?.image?.url}
		/>
	);

	const imgCloseButton = (className) => (
		<Link
			to={closeURL}
			className={className || ''}
			onClick={clearImgData}
			aria-label='Close'
		>
			<Icon
				name='close'
				className='!text-3xl'
			/>
		</Link>
	);

	return (
		<main
			id='template-ImageDetail'
			className='imageDetail h-screen bg-white'
		>
			<Container
				fluid
				noPadding
			>
				<div className='flex items-center justify-between px-6 py-4 lg:hidden'>
					<div className='relative [&>div]:w-auto [&_svg]:!mr-4 md:[&_svg]:!mr-6'>{socialBlock}</div>
					{closeURL && <div>{imgCloseButton('imageDetail-close text-white hover:cursor-pointer no-underline bg-neutral-750 flex items-center justify-center py-1 px-2')}</div>}
				</div>

				<div className='relative'>
					<Row
						noGutters
						className='max-w-full lg:h-screen'
					>
						<Column
							xs={12}
							lg={8}
							xxl={9}
							className='imageDetail-slideWrapper flex h-[400px] items-center justify-center bg-neutral-700 sm:h-[500px] lg:h-auto'
						>
							{prevURL && (
								<Link
									className='imageDetail-slidePrev absolute left-0 top-1/2 z-2 -translate-y-1/2 !leading-none text-neutral-200 hover:text-primary'
									to={prevURL}
									aria-label='Previous'
								>
									<Icon
										name='keyboard_arrow_left'
										className='!text-5xl lg:!text-7xl'
									/>
								</Link>
							)}

							<div className='relative inline-block'>
								{(detail_type === 'ugc' || detail_type === 'branch') && <div className='imageDetail-ribbon absolute bottom-12 right-0 z-2 bg-primary px-8 text-center text-sm font-semibold uppercase !leading-[40px] text-black shadow-xl md:px-12'>Customer Image</div>}
								{image?.[0] && (
									<Image
										className='imageDetail-image h-auto max-h-[400px] w-auto max-w-full sm:max-h-[500px] lg:max-h-screen'
										data={image[0]}
										objectFit='contain'
										loading='eager'
										onPointHover={setHover}
										activeHover={hover}
										showTile
									/>
								)}
							</div>

							{nextURL && (
								<Link
									className='imageDetail-slideNext absolute right-0 top-1/2 z-2 -translate-y-1/2 !leading-none text-neutral-200 hover:text-primary'
									to={nextURL}
									aria-label='Next'
								>
									<Icon
										name='keyboard_arrow_right'
										className='!text-5xl lg:!text-7xl'
									/>
								</Link>
							)}
						</Column>

						<Column
							xs={12}
							lg={4}
							xxl={3}
						>
							<div className='imageDetail-content flex h-full max-h-screen flex-col overflow-y-auto p-6 lg:px-10 lg:pb-8 lg:pt-16'>
								{heading && <h1 className='mb-0 text-xl !leading-tight md:text-2xl'>{heading}</h1>}
								{description && (
									<div
										className='my-4'
										dangerouslySetInnerHTML={{
											__html: description,
										}}
									/>
								)}
								{isValidArray(ctas) &&
									ctas.map((element, index) => (
										<p
											className={index > 0 ? 'mt-2.5' : ''}
											key={index}
										>
											<CTA
												className='!justify-start !text-left'
												data={element}
											>
												{element.link.title} &gt;
											</CTA>
										</p>
									))}

								{tiles?.heading && <p className='mb-1 mt-6 text-base font-semibold uppercase tracking-3px md:mt-8 md:text-lg xl:mt-6'>{tiles.heading}</p>}
								{isValidArray(tiles?.tile) && (
									<Row>
										{tiles.tile.map((tile, index) => (
											<Column
												xs={12}
												md={6}
												lg={12}
												key={index}
												className={`my-2`}
											>
												<Tile
													tile={tile}
													pdpURL={tile.static_url}
													variant='horizontal'
													onHover={setHover}
													wrapperClass={hover === tile.backend_name ? 'shadow-[0_0_0_6px_#ffde00] transition-shadow' : ''}
													loading
												/>
											</Column>
										))}
									</Row>
								)}

								{isValidArray(filter_cta) && (
									<>
										<p className='mb-1 mt-6 text-base font-semibold'>Related Content:</p>
										<List
											bullets='none'
											layout='inline'
										>
											{filter_cta.map((element, index) => (
												<li
													key={index}
													className={`my-1 ${index + 1 !== filter_cta.length ? 'mr-3' : ''}`}
												>
													<CTA
														data={{
															...element,
															gtm_link_type: 'button',
															button_style: 'tertiary',
														}}
														className='text-xs'
													>
														{element.link.title}
													</CTA>
												</li>
											))}
										</List>
									</>
								)}

								<div className={`imageDetail-socials mt-auto hidden pt-8 text-black lg:block [&>div]:w-auto [&>div]:cursor-default [&>div]:justify-start [&_svg]:block [&_svg]:cursor-pointer`}>{socialBlock}</div>
							</div>
						</Column>
					</Row>

					{closeURL && imgCloseButton('imageDetail-close text-white hover:cursor-pointer no-underline bg-black hidden lg:flex items-center justify-center p-2.5 absolute top-0 right-0')}
				</div>
			</Container>
			{data.contentstackImageDetail && isValidArray(image) && (
				<SDImageDetail
					description={description}
					image={image?.[0]}
					name={heading}
					tiles={tiles}
					url={url}
				/>
			)}
		</main>
	);
};

ImageDetail.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default ImageDetail;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => {
	// eslint-disable-next-line react/prop-types -- fix this
	function strip_html_tags(str) {
		if (str === null || str === '') return false;
		else str = str.toString();
		return str.replace(/<[^>]*>/g, '');
	}

	return (
		<SEO
			// eslint-disable-next-line react/prop-types -- fix this
			title={data.contentstackImageDetail.heading}
			// eslint-disable-next-line react/prop-types -- fix this
			description={strip_html_tags(data.contentstackImageDetail.description)}
			// eslint-disable-next-line react/prop-types -- fix this
			image={data.contentstackImageDetail.image?.[0]?.image?.url}
		/>
	);
};

export const query = graphql`
	query ($uid: String!) {
		contentstackImageDetail(uid: { eq: $uid }) {
			...ImageDetail
			tiles {
				heading
				tile {
					...Tile
				}
			}
		}

		config: contentstackImageDetailConfig {
			return_url
		}
	}
`;
